import { useContext, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { useTranslation } from 'react-i18next'
import ComplianceStatusChip from './ComplianceStatusChip'
import UploadComplianceDocumentDialog from './UploadComplianceDocumentDialog'
import DocumentPreviewDialog from './DocumentPreviewDialog'
import CPEditIconButton from '../../../Utils/CPEditIconButton'
import CPDeleteIconButton from '../../../Utils/CPDeleteIconButton'
import { authContext } from '../../../../contexts/AuthContext'
import {
  listSocietyComplianceDocuments,
  uploadSocietyComplianceDocument,
  unlinkSocietyComplianceDocument,
  deleteSocietyComplianceDocument,
  generateSocietyComplianceDocumentUrl
} from '../../../../services/societyComplianceDocuments'

function formatDueDate(dueDate, status, t) {
  if (!dueDate) return t('pending')
  const date = new Date(dueDate)
  const diffDays = Math.round((date - new Date()) / (1000 * 60 * 60 * 24))
  const formatted = date.toLocaleDateString()

  if (status === 'complete') return formatted
  if (diffDays < 0) return `${formatted} (${t('overdue')})`
  if (diffDays <= 30) return `${formatted} (${t('due_soon')})`
  return formatted
}

export default function ComplianceItemCard({ compliance, society, canEdit, onEdit, onDelete }) {
  const { t } = useTranslation()
  const { auth } = useContext(authContext)
  const [documents, setDocuments] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [previewDoc, setPreviewDoc] = useState(null)

  const name = society.language === 'es' ? compliance.name_es : compliance.name_en
  const description = society.language === 'es' ? compliance.description_es : compliance.description_en

  const fetchDocuments = () => {
    listSocietyComplianceDocuments(compliance.id)
      .then((data) => setDocuments(data || []))
      .catch(() => setDocuments([]))
  }

  useEffect(() => {
    fetchDocuments()
  }, [compliance.id])

  const handleUpload = async (file) => {
    await uploadSocietyComplianceDocument({
      file,
      societyLegalId: society.legal_id,
      complianceSocietyId: compliance.id,
      complianceId: compliance.id,
      loggedUserId: auth.user.id
    })
    fetchDocuments()
    setDialogOpen(false)
  }

  const handleDetach = async (doc) => {
    if (doc.is_linked) {
      await unlinkSocietyComplianceDocument(doc.id, auth.user.id)
    } else {
      await deleteSocietyComplianceDocument(doc.id, auth.user.id)
    }
    fetchDocuments()
  }

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        height: '100%'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
          {name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <ComplianceStatusChip status={compliance.status} />
          {canEdit && (
            <>
              <CPEditIconButton onClick={onEdit} sx={{ p: 0.5 }} />
              <CPDeleteIconButton onClick={onDelete} sx={{ p: 0.5 }} />
            </>
          )}
        </Box>
      </Box>

      {description && (
        <Typography variant='body2' color='text.secondary'>
          {description}
        </Typography>
      )}

      <Typography variant='caption' color='text.secondary'>
        {t('Due Date')}: {formatDueDate(compliance.due_date, compliance.status, t)}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        {documents.map((doc) => {
          const filename = doc.is_linked ? doc.linked_filename : doc.file_name
          return (
            <Chip
              key={doc.id}
              size='small'
              variant='outlined'
              clickable
              icon={<InsertDriveFileOutlinedIcon fontSize='small' />}
              label={filename}
              onClick={() => setPreviewDoc({ id: doc.id, filename })}
              onDelete={canEdit ? () => handleDetach(doc) : undefined}
            />
          )
        })}
      </Box>

      {canEdit && (
        <Button
          size='small'
          startIcon={<AttachFileIcon fontSize='small' />}
          onClick={() => setDialogOpen(true)}
          sx={{ alignSelf: 'flex-start', mt: 'auto', textTransform: 'none' }}
        >
          {t('attach_document')}
        </Button>
      )}

      <UploadComplianceDocumentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onUpload={handleUpload}
      />

      <DocumentPreviewDialog
        open={!!previewDoc}
        doc={previewDoc}
        fetchUrl={(d) => generateSocietyComplianceDocumentUrl(d.id)}
        onClose={() => setPreviewDoc(null)}
      />
    </Box>
  )
}
